// panels/SaveConfirmModal.jsx
import React from 'react';
import './SaveConfirmModal.css';

const SaveConfirmModal = ({ 
  topLevelItem, 
  modifiedParents, 
  diff, 
  pendingItems = [],
  modifiedItems = [],
  onConfirm, 
  onCancel 
}) => {
  const hasSubLevelChanges = modifiedParents.some(p => !p.isTargetItem);
  
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="save-confirm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Confirm Save</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Warning for sub-level changes */}
          {hasSubLevelChanges && (
            <div className="sublevel-alert">
              <div className="alert-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className="alert-content">
                <strong>Multiple BOMs will be updated</strong>
                <p>You've made changes to sub-assemblies. These changes will affect their BOMs even when used in other products.</p>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="save-summary">
            {topLevelItem && (
              <div className="summary-header">
                <span>Top-Level Item:</span>
                <strong>{topLevelItem.itemCode}</strong>
              </div>
            )}
            
            <div className="total-changes">
              <div className="change-stat items-new">
                <span className="stat-value">{pendingItems.length}</span>
                <span className="stat-label">New Items</span>
              </div>
              <div className="change-stat items-edited">
                <span className="stat-value">{modifiedItems.length}</span>
                <span className="stat-label">Items Edited</span>
              </div>
              <div className="change-stat created">
                <span className="stat-value">{diff?.created?.length || 0}</span>
                <span className="stat-label">BOM Links</span>
              </div>
              <div className="change-stat updated">
                <span className="stat-value">{diff?.updated?.length || 0}</span>
                <span className="stat-label">Qty Updated</span>
              </div>
              <div className="change-stat deleted">
                <span className="stat-value">{diff?.deleted?.length || 0}</span>
                <span className="stat-label">Deleted</span>
              </div>
            </div>
          </div>

          {/* New Items Section */}
          {pendingItems.length > 0 && (
            <div className="items-section">
              <h3>New Items to Create</h3>
              <div className="items-list">
                {pendingItems.map(item => (
                  <div key={item.id} className="item-row new">
                    <span className="item-code">{item.item_code}</span>
                    <span className="item-name">{item.name}</span>
                    <span className="item-type">{item.item_type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modified Items Section */}
          {modifiedItems.length > 0 && (
            <div className="items-section">
              <h3>Items to Update</h3>
              <div className="items-list">
                {modifiedItems.map(item => (
                  <div key={item.id} className="item-row modified">
                    <span className="item-code">{item.item_code}</span>
                    <span className="item-name">{item.name}</span>
                    <span className="item-type">{item.item_type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-BOM breakdown */}
          {modifiedParents.length > 0 && (
            <div className="bom-breakdown">
              <h3>BOM Relationship Changes:</h3>
              <div className="bom-list">
                {modifiedParents.map(parent => (
                  <div 
                    key={parent.parentId} 
                    className={`bom-item ${parent.isTargetItem ? 'is-top-level' : 'is-sublevel'}`}
                  >
                    <div className="bom-header">
                      <span className="bom-code">{parent.parentCode}</span>
                      {parent.isTargetItem ? (
                        <span className="bom-badge top-level">TOP-LEVEL</span>
                      ) : (
                        <span className="bom-badge sublevel">SUB-LEVEL</span>
                      )}
                    </div>
                    <div className="bom-changes">
                      {parent.created > 0 && (
                        <span className="change created">+{parent.created} new</span>
                      )}
                      {parent.updated > 0 && (
                        <span className="change updated">{parent.updated} qty</span>
                      )}
                      {parent.deleted > 0 && (
                        <span className="change deleted">-{parent.deleted} removed</span>
                      )}
                    </div>
                    
                    {diff?.byParent[parent.parentId] && (
                      <div className="bom-details">
                        {diff.byParent[parent.parentId].created.map((c, i) => (
                          <div key={`c-${i}`} className="detail-line created">
                            + {c.childCode} (qty: {c.quantity})
                          </div>
                        ))}
                        {diff.byParent[parent.parentId].updated.map((u, i) => (
                          <div key={`u-${i}`} className="detail-line updated">
                            ~ {u.childCode}: {u.oldQuantity} -&gt; {u.quantity}
                          </div>
                        ))}
                        {diff.byParent[parent.parentId].deleted.map((d, i) => (
                          <div key={`d-${i}`} className="detail-line deleted">
                            - {d.childCode}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-save" onClick={onConfirm}>
            Save All Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveConfirmModal;